/**
 * Badge de Status da Câmera
 * Mostra status visual da câmera com cores baseado em last_seen (atividade real)
 */

import React from 'react';

/**
 * Calcula o status de conectividade real baseado no last_seen
 * 🟢 ONLINE:  last_seen < 10 min
 * 🟡 ALERTA:  last_seen 10-30 min
 * 🔴 OFFLINE: last_seen > 30 min ou nunca
 */
export function getCameraConnectivityStatus(lastSeen?: string | null): 'online' | 'warning' | 'offline' {
  if (!lastSeen) return 'offline';
  
  const lastSeenDate = new Date(lastSeen);
  const now = new Date();
  const diffMinutes = (now.getTime() - lastSeenDate.getTime()) / (1000 * 60);
  
  if (diffMinutes < 10) return 'online';
  if (diffMinutes < 30) return 'warning';
  return 'offline';
}

/**
 * Formata tempo relativo em português
 */
export function formatLastSeen(lastSeen?: string | null): string {
  if (!lastSeen) return 'Nunca conectada';
  
  const lastSeenDate = new Date(lastSeen);
  const now = new Date();
  const diffMs = now.getTime() - lastSeenDate.getTime();
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffMinutes < 1) return 'Agora mesmo';
  if (diffMinutes < 60) return `${diffMinutes} min atrás`;
  if (diffHours < 24) return `${diffHours}h atrás`;
  if (diffDays === 1) return 'Ontem';
  return `${diffDays} dias atrás`;
}

interface CameraStatusBadgeProps {
  status: 'active' | 'inactive' | 'error' | 'maintenance' | string;
  isCapturing?: boolean;
  lastSeen?: string | null;
  className?: string;
  /** ✅ Câmera desabilitada (enabled=false): NÃO deve aparecer como offline */
  disabled?: boolean;
}

export const CameraStatusBadge: React.FC<CameraStatusBadgeProps> = ({
  status,
  isCapturing = false,
  lastSeen,
  className = '',
  disabled = false,
}) => {
  // Prioridade: Se tem last_seen, usar status de conectividade real
  const connectivity = getCameraConnectivityStatus(lastSeen);
  const hasLastSeen = !!lastSeen;

  const getStatusColor = () => {
    // ⛔ Câmera desabilitada tem prioridade: nunca mostra offline/online
    if (disabled) return 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600';

    if (isCapturing) return 'bg-green-100 text-green-800 border-green-200';
    
    // Se temos last_seen, usar status de conectividade real
    if (hasLastSeen) {
      switch (connectivity) {
        case 'online':
          return 'bg-green-100 text-green-800 border-green-200';
        case 'warning':
          return 'bg-yellow-100 text-yellow-800 border-yellow-200';
        case 'offline':
          return 'bg-red-100 text-red-800 border-red-200';
      }
    }
    
    // Fallback: usar status do campo status
    switch (status) {
      case 'active':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'error':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusText = () => {
    // ⛔ Câmera desabilitada tem prioridade: nunca mostra offline/online
    if (disabled) return '⛔ Desabilitada';

    if (isCapturing) return '🎥 Capturando';
    
    if (hasLastSeen) {
      switch (connectivity) {
        case 'online':
          return '🟢 Online';
        case 'warning':
          return '🟡 Alerta';
        case 'offline':
          return '🔴 Offline';
      }
    }
    
    switch (status) {
      case 'active':
        return '✓ Ativa';
      case 'inactive':
        return '○ Inativa';
      case 'error':
        return '✗ Erro';
      case 'maintenance':
        return '⚙ Manutenção';
      default:
        return status;
    }
  };

  const getStatusIcon = () => {
    // ⛔ Câmera desabilitada tem prioridade: nunca mostra offline/online
    if (disabled) return '⛔';

    if (isCapturing) return '⚡';
    
    if (hasLastSeen) {
      switch (connectivity) {
        case 'online':
          return '●';
        case 'warning':
          return '◐';
        case 'offline':
          return '○';
      }
    }
    
    switch (status) {
      case 'active':
        return '●';
      case 'inactive':
        return '○';
      case 'error':
        return '⚠';
      case 'maintenance':
        return '🔧';
      default:
        return '?';
    }
  };

  const lastSeenText = formatLastSeen(lastSeen);

  return (
    <div className={`flex flex-col items-end gap-1 ${className}`}>
      <span
        className={`
          inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border
          ${getStatusColor()}
        `}
        title={disabled ? 'Câmera desabilitada (não participa de captura)' : lastSeen ? `Última atividade: ${new Date(lastSeen).toLocaleString('pt-BR')}` : 'Sem dados de atividade'}
      >
        <span className="text-sm">{getStatusIcon()}</span>
        {getStatusText()}
      </span>
      {hasLastSeen && !disabled && (
        <span className="text-[10px] text-gray-500 dark:text-gray-400">
          {lastSeenText}
        </span>
      )}
    </div>
  );
};
